import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import * as React from "react";

import { ButtonProps, buttonVariants } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePagination } from "@/hooks/use-pagination";
import { cn } from "@/lib/utils";

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
);
Pagination.displayName = "Pagination";

type PaginationContentProps = React.ComponentProps<"ul"> & {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  siblingCount?: number;
  itemsPerPage?: number;
  itemsPerPageOptions?: number[];
  onItemsPerPageChange?: (limit: number) => void;
  totalItems?: number;
};

const PaginationContent = React.forwardRef<HTMLUListElement, PaginationContentProps>(
  (
    {
      className,
      currentPage,
      totalPages,
      onPageChange,
      siblingCount = 1,
      itemsPerPage,
      itemsPerPageOptions,
      onItemsPerPageChange,
      totalItems,
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const paginationRange = usePagination({ totalPages, currentPage, siblingCount });

    const renderPageNumbers = () => {
      if (isMobile) {
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              Página {currentPage} de {totalPages}
            </span>
          </div>
        );
      }

      return paginationRange.map((page, index) => {
        if (page === "ellipsis") {
          return (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          );
        }

        return (
          <PaginationItem key={page}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onPageChange(page as number);
              }}
              isActive={page === currentPage}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        );
      }) as React.ReactNode[];
    };

    return (
      <div
        className={cn(
          `flex w-full items-center ${isMobile ? "flex-col gap-4 rounded-lg border p-4" : "justify-between"}`,
          className,
        )}
      >
        {itemsPerPageOptions && onItemsPerPageChange && itemsPerPage && (
          <div
            className={`flex items-center gap-2 ${isMobile ? "order-2 w-full justify-between" : "order-1"}`}
          >
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              Itens por página:
            </span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(value) => onItemsPerPageChange(Number(value))}
            >
              <SelectTrigger id="pagination-items-per-page" className="h-8 w-[70px]">
                <SelectValue placeholder={itemsPerPage.toString()} />
              </SelectTrigger>
              <SelectContent>
                {itemsPerPageOptions.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div
          className={`flex items-center ${isMobile ? "order-1 w-full justify-between" : "order-2 gap-1"}`}
        >
          <PaginationPrevious
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (currentPage > 1) {
                onPageChange(currentPage - 1);
              }
            }}
            disabled={currentPage === 1}
            className={cn(
              isMobile ? "h-10 px-2 md:px-4" : "w-auto",
              currentPage === 1 && "pointer-events-none opacity-50",
            )}
          />

          {!isMobile && (
            <ul ref={ref} className={cn("flex items-center gap-1", className)} {...props}>
              {renderPageNumbers()}
            </ul>
          )}

          {isMobile && renderPageNumbers()}

          <PaginationNext
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (currentPage < totalPages) {
                onPageChange(currentPage + 1);
              }
            }}
            disabled={currentPage === totalPages}
            className={cn(
              isMobile ? "h-10 px-2 md:px-4" : "w-auto",
              currentPage === totalPages && "pointer-events-none opacity-50",
            )}
          />
        </div>

        {totalItems !== undefined && totalPages > 0 && (
          <span
            className={cn(
              "whitespace-nowrap text-sm text-muted-foreground",
              isMobile ? "hidden" : "order-3",
            )}
          >
            Página {currentPage} de {totalPages} ({totalItems} itens)
          </span>
        )}
      </div>
    );
  },
);
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn("", className)} {...props} />,
);
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
  disabled?: boolean;
} & Pick<ButtonProps, "size"> &
  React.ComponentProps<"a">;

const PaginationLink = ({
  className,
  isActive,
  size = "icon",
  disabled = false,
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    aria-disabled={disabled || undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? "outline" : "ghost",
        size,
      }),
      disabled && "cursor-not-allowed opacity-50",
      className,
    )}
    onClick={(e) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (props.onClick) props.onClick(e);
    }}
    {...props}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Ir para a página anterior"
    size="default"
    className={cn("gap-1 pl-2.5", className)}
    {...props}
  >
    <ChevronLeft className="h-4 w-4" />
    <span>Anterior</span>
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Ir para a próxima página"
    size="default"
    className={cn("gap-1 pr-2.5", className)}
    {...props}
  >
    <span>Próximo</span>
    <ChevronRight className="h-4 w-4" />
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn(
      "flex h-9 w-9 items-center justify-center text-gray-400 transition-colors duration-150",
      className,
    )}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">Mais páginas</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
